import * as React from "react";
import { useEffect, useState, useContext } from "react";
import {
  Button,
  Modal,
  Steps,
  Input,
  Form,
  Result,
  Space,
  Select,
  message,
} from "antd";
import { useStepsForm } from "sunflower-antd";
import type { SelectProps } from "antd";
import { AuthContext } from "../../../context/AuthContext";
import { toast } from "../../../utils/toast";
import {
  getGroupsApi,
} from "../../../services/Group/GroupApis";
import { getGroupsApi as getAdminGroupsApi } from "../../../services/Admin/AdminApis";
import { getRolesApi } from "../../../services/User/UserApis";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  user?: any;
  isAdmin?: boolean;
  showModal: Function;
  handleAdd: Function;
}

let options: SelectProps["options"] = [];
let roleOptions: SelectProps["options"] = [];

const UserModalForm: React.FC<IModalProps> = ({
  show,
  user,
  isAdmin,
  showModal,
  handleAdd,
}) => {
  const { currentGroup, currentUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const fetchGroups = async () => {
    try {
      if (isAdmin) {
        const { data } = await getAdminGroupsApi();

        if (!data || data.length === 0) {
          toast.warning({
            title: 'No Groups Available',
            description: 'No groups have been created yet. Please create at least one group before adding users.',
            duration: 8,
          });
          options = [];
          return;
        }

        options = data.map((group: any) => {
          return {
            value: group.id,
            label: group.GroupName,
          };
        });
      } else {
        const { data } = await getGroupsApi();

        if (!data || data.length === 0) {
          toast.warning({
            title: 'No Groups Available',
            description: 'No linked groups are available. Please create or link groups before adding users.',
            duration: 8,
          });
          options = [];
          return;
        }

        options = data.map((group: any) => {
          return {
            value: group.id,
            label: group.GroupName,
          };
        });
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error({
        title: 'Error Loading Groups',
        description: 'Failed to load groups. Please try again or contact support if the issue persists.',
        duration: 6,
      });
      options = [];
    }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await getRolesApi();
      const isSuperAdmin = currentUser?.Role.Name === "Super Admin";

      const filteredRoles = data.filter((role: any) => {
        return isSuperAdmin || role.Name !== "Super Admin";
      });

      if (!filteredRoles || filteredRoles.length === 0) {
        toast.warning({
          title: 'No Roles Available',
          description: 'No roles are configured in the system. Please contact an administrator.',
          duration: 8,
        });
        roleOptions = [];
        return;
      }

      roleOptions = filteredRoles.map((role: any) => {
        return {
          value: role.id,
          label: role.Name,
        };
      });
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error({
        title: 'Error Loading Roles',
        description: 'Failed to load user roles. Please try again or contact support.',
        duration: 6,
      });
      roleOptions = [];
    }
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      const response = await handleAdd(values);
      setLoading(true);

      if (response.success) {
        setLoading(false);
        return true;
      }
      setLoading(false);
      setErrorMsg(response.message);
      return false;
    },
    total: 4,
  });

  // Step Styling
  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 17 },
    labelAlign: "right" as const,
  };

  const tailLayout = {
    labelCol: { span: 6 },
    wrapperCol: { offset: 6, span: 17 },
  };

  const formList = [
    <>
      <Form.Item
        name="Name"
        label="Name"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Name" />
      </Form.Item>
      <Form.Item
        name="Surname"
        label="Surname"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Surname" />
      </Form.Item>
      <Form.Item
        name="Cellphone"
        label="Cellphone"
        rules={[
          {
            required: true,
            pattern: new RegExp(/^(?:\+27|0)[1-9]\d{8}$/),
            message: "Please enter your Cellphone Number",
          },
        ]}
      >
        <Input size="large" placeholder="Cellphone" />
      </Form.Item>
      <Form.Item
        name="Email"
        label="Email"
        rules={[
          {
            required: true,
            type: "email",
          },
        ]}
      >
        <Input size="large" placeholder="Email" />
      </Form.Item>

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Space>
      </Form.Item>
    </>,

    <>
      <Form.Item
        name="Group"
        label="Groups"
        rules={[
          {
            required: true,
            message: 'Please select at least one group',
          },
        ]}
      >
        <Select
          mode="tags"
          style={{ width: "100%" }}
          size="large"
          placeholder={options.length === 0 ? "No groups available" : "Select Groups"}
          options={options}
          loading={fetchingData}
          disabled={options.length === 0}
          notFoundContent={options.length === 0 ? "No groups configured. Please create groups first." : "No matching groups"}
        />
      </Form.Item>
      <Form.Item
        name="Role"
        label="Role"
        rules={[
          {
            required: true,
            message: 'Please select a role',
          },
        ]}
      >
        <Select
          style={{ width: "100%" }}
          size="large"
          placeholder={roleOptions.length === 0 ? "No roles available" : "Select Role"}
          options={roleOptions}
          loading={fetchingData}
          disabled={roleOptions.length === 0}
          notFoundContent={roleOptions.length === 0 ? "No roles configured. Please contact administrator." : "No matching roles"}
        />
      </Form.Item>
      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            style={{ marginBottom: 10 }}
            block
            size="large"
            loading={formLoading}
            onClick={() => {
              submit().then((result) => {
                if (result) {
                  gotoStep(current + 1);
                } else if (!result) {
                  gotoStep(current + 2);
                }
              });
            }}
          >
            Submit
          </Button>
        </Space>
      </Form.Item>
    </>,
  ];

  useEffect(() => {
    const loadData = async () => {
      setFetchingData(true);
      await Promise.all([fetchGroups(), fetchRoles()]);
      setFetchingData(false);
    };
    
    if (show) {
      loadData();
    }
  }, [show]);

  return (
    <Modal
      title="New User"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={900}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 2 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Basic Information" />
              <Step title="Select Group" />
            </Steps>
          </div>
        )}

        <div style={{ flex: 1, marginTop: 24 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 1100 }}>
            {formList[current]}
          </Form>

          {current === 2 && (
            <Result status="success" title="Successfully submitted.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleModal}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 3 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserModalForm;
